/**
 * KpiSparkline — inline SVG sparkline for KPI cards (v2 Dashboard).
 *
 * Renders the canonical sparkline from design/v2-direction/index.html
 * `sparkline()` function (lines 1464–1479):
 *   • preserveAspectRatio="none", 40px tall, full-width
 *   • smooth cubic-bezier path (Catmull-Rom approximation)
 *   • stroke-width 1.8, round caps/joins
 *   • under-fill = vertical linear gradient cvar/0.30 → cvar/0
 *   • stroke color = var(cvar) (raw — tokens resolve to HEX, no hsl() wrap)
 *
 * Data honesty contract (spec §2.3 hard constraint):
 *   - Plot REAL series only where one exists.
 *   - If no trend series exists, a flat line at the single current value
 *     is rendered (NOT a fabricated wiggle / fake upward trend).
 *   - A thin honest flat line is explicitly acceptable per spec.
 *
 * Token discipline: chart series color passed as a CSS var name string,
 * e.g. "--chart-1". No hex in this file. Guard-clean.
 */
import { useMemo, useId } from "react";

interface KpiSparklineProps {
  /**
   * Data points for the sparkline. Must have ≥2 elements.
   * If pts is [] or a single point, a flat line is drawn.
   * Values are y-values; x is evenly distributed across the width.
   */
  pts: number[];
  /**
   * CSS var name for the chart series color, e.g. "--chart-1".
   * Used raw as `var(cvar)` for stroke + color-mix for gradient (tokens are HEX).
   */
  cvar?: string;
  /** Height in pixels. Default 40. */
  h?: number;
  className?: string;
}

/** Smooth path via Catmull-Rom → Bezier approximation (matches canonical smoothPath). */
function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    return `M ${points[0]!.x.toFixed(1)} ${points[0]!.y.toFixed(1)}`;
  }
  let d = `M ${points[0]!.x.toFixed(1)} ${points[0]!.y.toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx.toFixed(1)} ${prev.y.toFixed(1)}, ${cpx.toFixed(1)} ${curr.y.toFixed(1)}, ${curr.x.toFixed(1)} ${curr.y.toFixed(1)}`;
  }
  return d;
}

export function KpiSparkline({
  pts,
  cvar = "--chart-1",
  h = 40,
  className,
}: KpiSparklineProps): JSX.Element {
  const gradId = useId().replace(/:/g, "");

  const paths = useMemo(() => {
    const W = 140;
    const H = h;
    const pad = 3;

    // Flatten to a single-point if empty
    const data = pts.length === 0 ? [0] : pts;

    // Compute a flat line if only one point OR if all points are equal (Open roles = 4,4,4,4)
    const max = Math.max(...data);
    const min = Math.min(...data);
    const isFlat = max === min; // single value or all-equal honest series
    const rng = max - min || 1; // prevent division by zero → flat line when all equal

    const mapped: { x: number; y: number }[] = data.map((v, i) => ({
      x: pad + (i * (W - 2 * pad)) / Math.max(data.length - 1, 1),
      // A truly flat series is centered vertically (mid-card) rather than
      // pinned to the bottom edge — an honest flat line that still reads as a
      // chart, not a broken baseline.
      y: isFlat ? H / 2 : pad + (1 - (v - min) / rng) * (H - 2 * pad),
    }));

    // For single-value or flat series: keep all y at the mid-point so it's
    // truly flat, not a degenerate NaN (rng guard above handles this via /1).
    const line = smoothPath(mapped);
    const last = mapped[mapped.length - 1]!;
    const first = mapped[0]!;
    const area =
      line +
      ` L ${last.x.toFixed(1)} ${(H - pad).toFixed(1)} L ${first.x.toFixed(1)} ${(H - pad).toFixed(1)} Z`;

    return { line, area };
  }, [pts, h]);

  return (
    <div className={className} style={{ height: `${h}px` }}>
      <svg
        viewBox={`0 0 140 ${h}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: `${h}px`, display: "block" }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: `color-mix(in srgb, var(${cvar}) 30%, transparent)` }} />
            <stop offset="100%" style={{ stopColor: "transparent" }} />
          </linearGradient>
        </defs>
        <path d={paths.area} fill={`url(#${gradId})`} />
        <path
          d={paths.line}
          fill="none"
          style={{ stroke: `var(${cvar})` }}
          strokeWidth="1.8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
